import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Instagram, Linkedin, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export const ContactSection = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke("submit-lead", {
        body: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone || undefined,
          message: formData.message,
          source: "contact_form",
        },
      });

      if (error) throw error;

      toast({
        title: "Inquiry Received",
        description: "Thank you for your interest. We will respond within 24 hours.",
      });
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error: any) {
      console.error("Error submitting form:", error);
      const errorMessage = error?.message?.includes("rate limit") 
        ? "Too many submissions. Please try again later."
        : "Failed to submit inquiry. Please try again.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-charcoal text-cream">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left: Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="font-body text-xs uppercase tracking-[0.4em] text-gold-light/80 mb-4">
              Begin the Conversation
            </p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light mb-8 leading-tight">
              Where Legacy
              <span className="block italic font-medium">Begins</span>
            </h2>
            
            <p className="font-body text-cream/70 leading-relaxed mb-12 max-w-md">
              Every exceptional acquisition starts with a conversation. 
              Share your vision, and let us show you what becomes possible 
              when expertise meets opportunity.
            </p>

            {/* Contact Details */}
            <div className="space-y-6 mb-12">
              <motion.a
                href="mailto:ask@agotaestate.com"
                whileHover={{ x: 5 }}
                transition={{ duration: 0.2 }}
                className="flex items-center gap-4 text-cream/80 hover:text-gold-light transition-colors group"
              >
                <div className="w-10 h-10 rounded-full bg-cream/5 flex items-center justify-center group-hover:bg-gold-light/20 transition-colors duration-300">
                  <Mail size={18} className="text-gold-light" />
                </div>
                <span className="font-body">ask@agotaestate.com</span>
              </motion.a>
              <motion.a
                href="tel:+37060044499"
                whileHover={{ x: 5 }}
                transition={{ duration: 0.2 }}
                className="flex items-center gap-4 text-cream/80 hover:text-gold-light transition-colors group"
              >
                <div className="w-10 h-10 rounded-full bg-cream/5 flex items-center justify-center group-hover:bg-gold-light/20 transition-colors duration-300">
                  <Phone size={18} className="text-gold-light" />
                </div>
                <span className="font-body">+370 600 44 499</span>
              </motion.a>
              <motion.div
                initial={{ opacity: 1 }}
                className="flex items-center gap-4 text-cream/80"
              >
                <div className="w-10 h-10 rounded-full bg-cream/5 flex items-center justify-center">
                  <MapPin size={18} className="text-gold-light" />
                </div>
                <span className="font-body">Klaipėda, Lithuania • Flexible Worldwide</span>
              </motion.div>
            </div>

            {/* Social Links - Coming Soon */}
            <div className="flex gap-4">
              <motion.div
                whileHover={{ scale: 1.1, borderColor: "hsl(40 45% 75%)" }}
                transition={{ duration: 0.2 }}
                className="w-12 h-12 border border-cream/20 flex items-center justify-center text-cream/40 cursor-default rounded-full"
                title="Coming soon"
              >
                <Instagram size={20} />
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.1, borderColor: "hsl(40 45% 75%)" }}
                transition={{ duration: 0.2 }}
                className="w-12 h-12 border border-cream/20 flex items-center justify-center text-cream/40 cursor-default rounded-full"
                title="Coming soon"
              >
                <Linkedin size={20} />
              </motion.div>
            </div>
          </motion.div>

          {/* Right: Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="font-body text-sm uppercase tracking-[0.1em] text-cream/60 mb-2 block">
                    Full Name
                  </label>
                  <Input
                    type="text"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="bg-transparent border-cream/20 text-cream placeholder:text-cream/40 focus:border-gold-light h-12"
                  />
                </div>
                <div>
                  <label className="font-body text-sm uppercase tracking-[0.1em] text-cream/60 mb-2 block">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="bg-transparent border-cream/20 text-cream placeholder:text-cream/40 focus:border-gold-light h-12"
                  />
                </div>
              </div>

              <div>
                <label className="font-body text-sm uppercase tracking-[0.1em] text-cream/60 mb-2 block">
                  Phone Number
                </label>
                <Input
                  type="tel"
                  placeholder="+1 234 567 890"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-transparent border-cream/20 text-cream placeholder:text-cream/40 focus:border-gold-light h-12"
                />
              </div>

              <div>
                <label className="font-body text-sm uppercase tracking-[0.1em] text-cream/60 mb-2 block">
                  Your Message
                </label>
                <Textarea
                  placeholder="Tell us about your investment interests and preferred locations..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={5}
                  className="bg-transparent border-cream/20 text-cream placeholder:text-cream/40 focus:border-gold-light resize-none"
                />
              </div>

              <Button 
                type="submit" 
                variant="luxury" 
                size="xl" 
                className="w-full sm:w-auto" 
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Inquiry
                    <ArrowRight size={18} />
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
